import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, IconButton, MenuItem, Paper, Switch,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PublishIcon from '@mui/icons-material/Publish';
import QuizIcon from '@mui/icons-material/Quiz';
import ReplayIcon from '@mui/icons-material/Replay';
import UnpublishedIcon from '@mui/icons-material/Unpublished';
import { lmsService, staffService } from '../../services/api';

const emptyQ = () => ({
  type: 'mc',
  question: '',
  options: ['', '', '', ''],
  correct_index: 0,
  correct_indexes: [],
  model_answer: '',
  explanation: '',
});

const emptyForm = {
  title: '',
  difficulty: '',
  published: false,
  allow_retake: false,
  course: '',
  material: '',
  pass_mark: 60,
  max_attempts: '',
  questions: [emptyQ()],
};

function canManageLms() {
  try {
    const u = JSON.parse(localStorage.getItem('currentUser') || 'null');
    const p = u?.effective_permissions || u?.role?.permissions || {};
    return p['menu.lms.manage'] === true;
  } catch {
    return false;
  }
}

export default function LmsTestsPage() {
  const navigate = useNavigate();
  const manage = useMemo(() => canManageLms(), []);
  const [rows, setRows] = useState([]);
  const [courses, setCourses] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [grantOpen, setGrantOpen] = useState(false);
  const [grantQuiz, setGrantQuiz] = useState(null);
  const [staffList, setStaffList] = useState([]);
  const [grantStaffId, setGrantStaffId] = useState('');
  const [grantSaving, setGrantSaving] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      if (manage) {
        const [qRes, cRes, mRes] = await Promise.all([
          lmsService.quizzes(),
          lmsService.courses(),
          lmsService.materials(),
        ]);
        setRows(Array.isArray(qRes.data) ? qRes.data : []);
        setCourses(Array.isArray(cRes.data) ? cRes.data : []);
        setMaterials(Array.isArray(mRes.data) ? mRes.data : []);
      } else {
        const res = await lmsService.quizzes();
        setRows(Array.isArray(res.data) ? res.data : res.data?.results || []);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Помилка');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [manage]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = async (row) => {
    try {
      const res = await lmsService.quiz(row.id);
      const q = res.data;
      setEditingId(q.id);
      setForm({
        title: q.title || '',
        difficulty: q.difficulty || '',
        published: Boolean(q.published),
        allow_retake: Boolean(q.allow_retake),
        course: q.course || '',
        material: q.material || '',
        pass_mark: q.settings?.pass_mark ?? 60,
        max_attempts: q.settings?.max_attempts ?? '',
        questions: (q.questions || []).length ? q.questions : [emptyQ()],
      });
      setOpen(true);
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    }
  };

  const updateQuestion = (idx, patch) => {
    setForm((prev) => {
      const questions = prev.questions.map((q, i) => (i === idx ? { ...q, ...patch } : q));
      return { ...prev, questions };
    });
  };

  const generateWithAi = async () => {
    const topic = (form.title || '').trim() || window.prompt('Тема для генерації тесту?');
    if (!topic) return;
    setAiGenerating(true);
    try {
      const res = await lmsService.aiGenerateQuiz({
        topic,
        n: 5,
        difficulty: form.difficulty || 'середня',
        open_count: 1,
      });
      const qs = (res.data?.questions || []).map((q) => ({
        type: q.type || 'mc',
        question: q.question || '',
        options: q.options || ['', '', '', ''],
        correct_index: q.correct_index ?? 0,
        correct_indexes: q.correct_indexes || [],
        model_answer: q.model_answer || '',
        explanation: q.explanation || '',
      }));
      if (!qs.length) {
        alert('AI не повернув питань');
        return;
      }
      setForm((prev) => ({
        ...prev,
        title: prev.title || res.data?.title || topic,
        questions: qs,
      }));
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    } finally {
      setAiGenerating(false);
    }
  };

  const handleSave = async () => {
    if (saving) return;
    const title = (form.title || '').trim();
    if (!title) { alert('Вкажіть назву'); return; }
    setSaving(true);
    try {
      const questions = form.questions.map((q) => {
        const base = {
          type: q.type || 'mc',
          question: q.question || '',
          explanation: q.explanation || '',
        };
        if (base.type === 'open') {
          return { ...base, model_answer: q.model_answer || '' };
        }
        if (base.type === 'multi') {
          return {
            ...base,
            options: (q.options || []).filter((o) => String(o).trim()),
            correct_indexes: q.correct_indexes || [],
          };
        }
        return {
          ...base,
          options: (q.options || []).filter((o) => String(o).trim()),
          correct_index: Number(q.correct_index) || 0,
        };
      });
      const maxAttemptsRaw = String(form.max_attempts ?? '').trim();
      const maxAttempts = maxAttemptsRaw === '' ? null : Math.max(1, Number(maxAttemptsRaw) || 1);
      const payload = {
        title,
        difficulty: form.difficulty || '',
        published: Boolean(form.published),
        allow_retake: Boolean(form.allow_retake),
        course: form.course || null,
        material: form.material || null,
        visible_role_ids: [],
        questions,
        settings: {
          pass_mark: Number(form.pass_mark) || null,
          max_attempts: maxAttempts,
          show_answers: 'all',
          points_per_correct: 1,
        },
      };
      if (editingId) await lmsService.quizUpdate(editingId, payload);
      else await lmsService.quizCreate(payload);
      setOpen(false);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const visibleRows = manage
    ? rows
    : rows.filter((q) => q.published || q.can_retake != null);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <QuizIcon color="primary" />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Тести</Typography>
        </Box>
        {manage ? (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>Новий тест</Button>
        ) : null}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Назва</TableCell>
                <TableCell>Питань</TableCell>
                {manage ? <TableCell>Статус</TableCell> : <TableCell>Спроби</TableCell>}
                <TableCell align="right">Дії</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography color="text.secondary">
                      {manage ? 'Тестів ще немає — створіть перший.' : 'Немає доступних тестів.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : visibleRows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>
                    <Typography sx={{ fontWeight: 600 }}>{row.title}</Typography>
                    {row.course_title ? (
                      <Typography variant="caption" color="text.secondary" display="block">Курс: {row.course_title}</Typography>
                    ) : null}
                    {row.material_title ? (
                      <Typography variant="caption" color="text.secondary" display="block">Урок: {row.material_title}</Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>{row.n}</TableCell>
                  <TableCell>
                    {manage ? (
                      <Chip size="small" label={row.published ? 'Опубліковано' : 'Чернетка'} color={row.published ? 'success' : 'default'} />
                    ) : (
                      <Chip size="small" label={row.my_attempts || 0} />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {(row.published || !manage) ? (
                      <Button
                        size="small"
                        variant={manage ? 'outlined' : 'contained'}
                        sx={{ mr: 0.5 }}
                        disabled={!manage && !row.can_retake && (row.my_attempts || 0) > 0}
                        onClick={() => navigate(`/lms/tests/${row.id}`)}
                      >
                        {manage
                          ? 'Пройти'
                          : ((row.my_attempts || 0) > 0 ? 'Ще раз' : 'Пройти')}
                      </Button>
                    ) : null}
                    {manage ? (
                      <>
                        <IconButton size="small" onClick={() => openEdit(row)} title="Редагувати">
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          title="Дозвіл на перездачу"
                          onClick={async () => {
                            setGrantQuiz(row);
                            setGrantStaffId('');
                            setGrantOpen(true);
                            try {
                              const res = await staffService.getAll();
                              setStaffList(Array.isArray(res.data) ? res.data : res.data?.results || []);
                            } catch (_) {
                              setStaffList([]);
                            }
                          }}
                        >
                          <ReplayIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={async () => {
                            if (row.published) await lmsService.quizUnpublish(row.id);
                            else await lmsService.quizPublish(row.id);
                            load();
                          }}
                        >
                          {row.published ? <UnpublishedIcon fontSize="small" /> : <PublishIcon fontSize="small" />}
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={async () => {
                            if (!window.confirm('Видалити тест?')) return;
                            await lmsService.quizDelete(row.id);
                            load();
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={open} onClose={() => !saving && setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingId ? 'Редагувати тест' : 'Новий тест'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button variant="outlined" size="small" disabled={aiGenerating} onClick={generateWithAi}>
                {aiGenerating ? 'AI генерує…' : 'Згенерувати AI'}
              </Button>
            </Box>
            <TextField label="Назва" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} fullWidth required />
            <TextField
              select
              label="Курс (тест наприкінці курсу)"
              value={form.course}
              onChange={(e) => setForm({ ...form, course: e.target.value })}
              fullWidth
            >
              <MenuItem value="">— без курсу —</MenuItem>
              {courses.map((c) => <MenuItem key={c.id} value={c.id}>{c.title}</MenuItem>)}
            </TextField>
            <TextField
              select
              label="Матеріал / урок (тест наприкінці)"
              value={form.material}
              onChange={(e) => setForm({ ...form, material: e.target.value })}
              fullWidth
            >
              <MenuItem value="">— без матеріалу —</MenuItem>
              {materials.map((m) => (
                <MenuItem key={m.id} value={m.id}>
                  {m.title}{m.base ? ` (${m.base})` : ''}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Поріг проходження %"
              type="number"
              value={form.pass_mark}
              onChange={(e) => setForm({ ...form, pass_mark: e.target.value })}
              helperText="Напр. 60 — потрібно набрати ≥60%"
            />
            <FormControlLabel
              control={<Switch checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} />}
              label="Опубліковано"
            />
            <FormControlLabel
              control={<Switch checked={form.allow_retake} onChange={(e) => setForm({ ...form, allow_retake: e.target.checked })} />}
              label="Дозволити перездачу"
            />
            {form.allow_retake ? (
              <TextField
                label="Макс. спроб"
                type="number"
                value={form.max_attempts}
                onChange={(e) => setForm({ ...form, max_attempts: e.target.value })}
                helperText="Порожньо — без ліміту. Інакше після ліміту потрібен дозвіл на перездачу."
              />
            ) : null}

            {form.questions.map((q, idx) => (
              <Paper key={idx} variant="outlined" sx={{ p: 2 }}>
                <Typography sx={{ fontWeight: 600, mb: 1 }}>Питання {idx + 1}</Typography>
                <TextField
                  select
                  label="Тип"
                  value={q.type}
                  onChange={(e) => updateQuestion(idx, { type: e.target.value })}
                  sx={{ mb: 1, minWidth: 160 }}
                >
                  <MenuItem value="mc">Один варіант</MenuItem>
                  <MenuItem value="multi">Кілька варіантів</MenuItem>
                  <MenuItem value="open">Відкрите</MenuItem>
                </TextField>
                <TextField
                  label="Текст питання"
                  value={q.question}
                  onChange={(e) => updateQuestion(idx, { question: e.target.value })}
                  fullWidth
                  sx={{ mb: 1 }}
                />
                {q.type === 'open' ? (
                  <TextField
                    label="Еталонна відповідь"
                    value={q.model_answer || ''}
                    onChange={(e) => updateQuestion(idx, { model_answer: e.target.value })}
                    fullWidth
                  />
                ) : (
                  (q.options || ['', '', '', '']).map((opt, oi) => (
                    <Box key={oi} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                      <TextField
                        label={`Варіант ${oi + 1}`}
                        value={opt}
                        onChange={(e) => {
                          const options = [...(q.options || [])];
                          options[oi] = e.target.value;
                          updateQuestion(idx, { options });
                        }}
                        fullWidth
                      />
                      {q.type === 'mc' ? (
                        <FormControlLabel
                          control={
                            <Switch
                              checked={Number(q.correct_index) === oi}
                              onChange={() => updateQuestion(idx, { correct_index: oi })}
                            />
                          }
                          label="вірно"
                        />
                      ) : (
                        <FormControlLabel
                          control={
                            <Switch
                              checked={(q.correct_indexes || []).includes(oi)}
                              onChange={(e) => {
                                const set = new Set(q.correct_indexes || []);
                                if (e.target.checked) set.add(oi);
                                else set.delete(oi);
                                updateQuestion(idx, { correct_indexes: [...set] });
                              }}
                            />
                          }
                          label="вірно"
                        />
                      )}
                    </Box>
                  ))
                )}
                <Button
                  size="small"
                  color="error"
                  disabled={form.questions.length <= 1}
                  onClick={() => setForm((p) => ({ ...p, questions: p.questions.filter((_, i) => i !== idx) }))}
                >
                  Видалити питання
                </Button>
              </Paper>
            ))}
            <Button onClick={() => setForm((p) => ({ ...p, questions: [...p.questions, emptyQ()] }))}>
              + Питання
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>Скасувати</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Зберегти'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={grantOpen} onClose={() => !grantSaving && setGrantOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Перездача: {grantQuiz?.title || ''}</DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            label="Співробітник"
            value={grantStaffId}
            onChange={(e) => setGrantStaffId(e.target.value)}
            sx={{ mt: 1 }}
          >
            {staffList.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.full_name || s.username}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGrantOpen(false)} disabled={grantSaving}>Скасувати</Button>
          <Button
            variant="contained"
            disabled={grantSaving || !grantStaffId || !grantQuiz}
            onClick={async () => {
              setGrantSaving(true);
              try {
                await lmsService.quizGrant(grantQuiz.id, grantStaffId);
                setGrantOpen(false);
                alert('Дозвіл видано');
              } catch (e) {
                alert(e?.response?.data?.detail || e.message);
              } finally {
                setGrantSaving(false);
              }
            }}
          >
            {grantSaving ? <CircularProgress size={20} /> : 'Видати'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
